import React, { useEffect, useMemo, useState } from "react";
import { Fade, Popper } from "@mui/material";
import Paper from "@mui/material/Paper";
import { NodeTypes } from "./constants";
import { getData } from "../../apis/api";
import { ENDPOINTS } from "../../apis/urls";
import { useTheme } from "../../contexts/ThemeContext";

const TreeItem = ({
  itemId,
  label,
  children,
  onClick,
  isExpanded,
  onToggle,
  hasChildren = false,
  level = 0,
  darkMode = false,
  icon,
}) => {
  const handleClick = (e) => {
    e.stopPropagation();
    if (hasChildren) {
      onToggle(itemId);
    } else if (onClick) {
      onClick();
    }
  };

  const indentStyle = {
    paddingLeft: `${8 + level * 14}px`,
  };

  return (
    <div className="tree-item">
      <div
        className="tree-item-content"
        style={indentStyle}
        onClick={handleClick}
      >
        {hasChildren && (
          <span className={`tree-item-icon ${isExpanded ? "expanded" : ""}`}>
            <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
              <path
                d="M3 2L6 5L3 8"
                stroke="currentColor"
                strokeWidth="1.2"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </span>
        )}
        {!hasChildren && <span className="tree-item-spacer">{icon}</span>}
        <span className="tree-item-label">{label}</span>
      </div>
      {hasChildren && isExpanded && (
        <div className="tree-item-children">{children}</div>
      )}
    </div>
  );
};

// Steps the menu lists by hand; every other backend node type comes from the
// node catalogue (workflow/node_details.py) and is drawn by GenericNode.
const LISTED_NODE_TYPES = new Set([
  NodeTypes.Start,
  NodeTypes.OutboundCall,
  NodeTypes.SendSMS,
  NodeTypes.SendEmail,
  NodeTypes.Delay,
  NodeTypes.Conditional,
  NodeTypes.End,
  NodeTypes.HttpRequest,
]);

const CustomTreeView = ({ children, darkMode = false }) => {
  return <div className="custom-tree-view">{children}</div>;
};

export const PopperModal = ({
  open,
  anchorEl,
  onNodeAdded,
  onMessageNodeAdd,
  onConditionalNodeAdd,
  onDelayNodeAdd,
  onSendMessageNodeAdd,
}) => {
  const { darkMode } = useTheme();
  const [expandedItems, setExpandedItems] = useState(
    new Set(["communication", "logic", "integrations", "catalog:Social & Messaging"])
  );
  const [catalog, setCatalog] = useState([]);

  useEffect(() => {
    getData(ENDPOINTS.WORKFLOW.NODE_TYPES)
      .then((res) => setCatalog((res?.nodes || []).filter((n) => !LISTED_NODE_TYPES.has(n.nodeType))))
      .catch((e) => console.error("Failed to load workflow node catalogue:", e));
  }, []);

  const catalogGroups = useMemo(() => {
    const groups = new Map();
    catalog.forEach((n) => {
      const category = n.category || "More Steps";
      if (!groups.has(category)) groups.set(category, []);
      groups.get(category).push(n);
    });
    return Array.from(groups.entries());
  }, [catalog]);

  const handleToggle = (itemId) => {
    const newExpanded = new Set(expandedItems);
    if (newExpanded.has(itemId)) {
      newExpanded.delete(itemId);
    } else {
      newExpanded.add(itemId);
    }
    setExpandedItems(newExpanded);
  };

  return (
    <>
      <style jsx>{`
        .custom-popper-modal {
          min-width: 210px;
          max-width: 250px;
          border-radius: 10px;
          box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.15),
            0 8px 10px -6px rgba(0, 0, 0, 0.1);
          overflow: hidden;
          background-color: ${darkMode ? "#18181b" : "#ffffff"};
          border: 1px solid ${darkMode ? "#27272a" : "#e2e8f0"};
          position: relative;
        }

        .tree-item-content {
          display: flex;
          align-items: center;
          padding: 7px 10px;
          cursor: pointer;
          transition: background-color 0.12s ease-in-out;
          font-size: 13px;
          font-weight: 500;
          min-height: 30px;
          color: ${darkMode ? "#fafafa" : "#1e293b"};
        }

        .tree-item-content:hover {
          background-color: ${darkMode ? "#27272a" : "#f1f5f9"};
        }

        .tree-item-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 14px;
          height: 14px;
          margin-right: 6px;
          transition: transform 0.2s ease-in-out;
          color: ${darkMode ? "#a1a1aa" : "#64748b"};
        }

        .tree-item-icon.expanded {
          transform: rotate(90deg);
        }

        .tree-item-spacer {
          width: 18px;
          height: 14px;
          margin-right: 4px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
        }

        .tree-item-label {
          font-size: 13px;
          flex: 1;
          font-weight: 500;
        }

        .tree-item-children {
          background-color: ${darkMode
            ? "rgba(24, 24, 27, 0.5)"
            : "rgba(248, 250, 252, 0.7)"};
        }

        .tree-item-children .tree-item-content {
          font-weight: 400;
          font-size: 12px;
          color: ${darkMode ? "#d4d4d8" : "#475569"};
        }

        .tree-item-children .tree-item-content:hover {
          background-color: ${darkMode ? "#3f3f46" : "#e2e8f0"};
          color: ${darkMode ? "#ffffff" : "#0f172a"};
        }
      `}</style>

      <Popper
        open={open}
        anchorEl={anchorEl}
        placement="right-start"
        transition
        style={{ zIndex: 1300 }}
        disablePortal={false}
      >
        {({ TransitionProps }) => (
          <Fade {...TransitionProps} timeout={200}>
            <Paper
              className="custom-popper-modal"
              elevation={4}
              style={{
                maxHeight: "70vh",
                overflowY: "auto",
                backgroundColor: darkMode ? "#18181b" : "#ffffff",
                border: `1px solid ${darkMode ? "#27272a" : "#e2e8f0"}`,
              }}
            >
              <CustomTreeView darkMode={darkMode}>
                {/* Communication */}
                <TreeItem
                  itemId="communication"
                  label="Communication"
                  hasChildren={true}
                  isExpanded={expandedItems.has("communication")}
                  onToggle={handleToggle}
                  level={0}
                  darkMode={darkMode}
                >
                  <TreeItem
                    itemId={NodeTypes.OutboundCall}
                    label="Voice Bot Call"
                    onClick={() => onNodeAdded(NodeTypes.OutboundCall)}
                    level={1}
                    darkMode={darkMode}
                  />
                  <TreeItem
                    itemId={NodeTypes.SendSMS}
                    label="Send SMS"
                    onClick={() => onNodeAdded(NodeTypes.SendSMS)}
                    level={1}
                    darkMode={darkMode}
                  />
                  <TreeItem
                    itemId={NodeTypes.SendEmail}
                    label="Send Email"
                    onClick={() => onNodeAdded(NodeTypes.SendEmail)}
                    level={1}
                    darkMode={darkMode}
                  />
                </TreeItem>

                {/* Logic & Flow */}
                <TreeItem
                  itemId="logic"
                  label="Logic & Flow"
                  hasChildren={true}
                  isExpanded={expandedItems.has("logic")}
                  onToggle={handleToggle}
                  level={0}
                  darkMode={darkMode}
                >
                  <TreeItem
                    itemId={NodeTypes.Delay}
                    label="Delay / Hold"
                    onClick={() =>
                      onDelayNodeAdd
                        ? onDelayNodeAdd()
                        : onNodeAdded(NodeTypes.Delay)
                    }
                    level={1}
                    darkMode={darkMode}
                  />
                  <TreeItem
                    itemId={NodeTypes.Conditional}
                    label="Condition (If / Else)"
                    onClick={() =>
                      onConditionalNodeAdd
                        ? onConditionalNodeAdd()
                        : onNodeAdded(NodeTypes.Conditional)
                    }
                    level={1}
                    darkMode={darkMode}
                  />
                  <TreeItem
                    itemId={NodeTypes.End}
                    label="End Journey"
                    onClick={() => onNodeAdded(NodeTypes.End)}
                    level={1}
                    darkMode={darkMode}
                  />
                </TreeItem>

                {/* Integrations */}
                <TreeItem
                  itemId="integrations"
                  label="Integrations"
                  hasChildren={true}
                  isExpanded={expandedItems.has("integrations")}
                  onToggle={handleToggle}
                  level={0}
                  darkMode={darkMode}
                >
                  <TreeItem
                    itemId={NodeTypes.HttpRequest}
                    label="HTTP Webhook"
                    onClick={() => onNodeAdded(NodeTypes.HttpRequest)}
                    level={1}
                    darkMode={darkMode}
                  />
                </TreeItem>

                {/* Backend node catalogue */}
                {catalogGroups.map(([category, items]) => (
                  <TreeItem
                    key={category}
                    itemId={`catalog:${category}`}
                    label={category}
                    hasChildren={true}
                    isExpanded={expandedItems.has(`catalog:${category}`)}
                    onToggle={handleToggle}
                    level={0}
                    darkMode={darkMode}
                  >
                    {items.map((n) => (
                      <TreeItem
                        key={n.nodeType}
                        itemId={n.nodeType}
                        label={n.displayName || n.nodeType}
                        onClick={() => onNodeAdded(n.nodeType, n)}
                        level={1}
                        darkMode={darkMode}
                      />
                    ))}
                  </TreeItem>
                ))}

                {/* WhatsApp Chatbot (Legacy) */}
                <TreeItem
                  itemId="whatsapp_legacy"
                  label="WhatsApp Bot (Legacy)"
                  hasChildren={true}
                  isExpanded={expandedItems.has("whatsapp_legacy")}
                  onToggle={handleToggle}
                  level={0}
                  darkMode={darkMode}
                >
                  <TreeItem
                    itemId={NodeTypes.Text}
                    label="Text Message"
                    onClick={() =>
                      onMessageNodeAdd
                        ? onMessageNodeAdd()
                        : onNodeAdded(NodeTypes.Text)
                    }
                    level={1}
                    darkMode={darkMode}
                  />
                  <TreeItem
                    itemId={NodeTypes.SendMessage}
                    label="Send Message"
                    onClick={() =>
                      onSendMessageNodeAdd
                        ? onSendMessageNodeAdd()
                        : onNodeAdded(NodeTypes.SendMessage)
                    }
                    level={1}
                    darkMode={darkMode}
                  />
                  <TreeItem
                    itemId={NodeTypes.List}
                    label="Interactive List"
                    onClick={() => onNodeAdded(NodeTypes.List)}
                    level={1}
                    darkMode={darkMode}
                  />
                </TreeItem>
              </CustomTreeView>
            </Paper>
          </Fade>
        )}
      </Popper>
    </>
  );
};
